// Reconstruct the thread/lineage structure of one agentic turn from its
// per-call token observations -- no conversation bodies, no stored lineage
// key. The signal is Anthropic's cache accounting: within one conversation,
// each call re-reads everything the previous call held, so
//
//   cache_read(call N) ~= cache_read(N-1) + cache_write(N-1) + input(N-1)
//
// and every conversation forms its own arithmetic chain through the turn's
// interleaved calls. A call that matches no open chain starts a new lineage:
// a FRESH start (cache_read at system-prompt scale) is a genuine fork -- a
// sub-agent's first call -- while a LARGE unmatched start is a chain break
// (cache expiry, compaction, out-of-order capture) and is labeled a
// continuation rather than claimed as a fork. Validated against a real
// 420-call fleet turn: the main thread, its pre-compaction tail, and each
// worker resolve into separate chains, with every true spawn starting at the
// same fresh system-prompt cache size.
//
// This is an inference, not a record. Tolerances absorb cache-TTL edge noise;
// the output labels carry the uncertainty (fork vs continuation), and callers
// must not present a continuation as a proven spawn.

// Chain-match tolerance: absolute floor + a relative band. Cache re-reads
// are not byte-exact across calls (tool-result trimming, cache-block
// boundaries), but distinct threads differ by orders of magnitude more.
const TOL_ABS = 2048;
const TOL_REL = 0.02;
// A first call whose cache_read is at most this is reading nothing but the
// cached system prompt / tool defs -- a fresh conversation, i.e. a fork.
const FRESH_START_CEILING = 32_768;

const band = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * Group one turn's calls into lineages.
 *
 * `rows` are per-call records ascending in time; each needs `time` (ms) and
 * the token bands (`input`, `output`, `cacheRead`, `cacheWrite`) to chain.
 * Rows missing bands are collected into the `unchained` remainder rather
 * than guessed at.
 *
 * Returns null when fewer than MIN_CHAINABLE_SHARE of rows carry bands (the
 * caller should fall back to a flat listing). Otherwise:
 *   {
 *     lineages: [{ kind: "root"|"fork"|"continuation", rows, start, end,
 *                  calls, peakCtx }],   // by first-call time; root first
 *     unchained: [rows without bands],
 *   }
 */
export const MIN_CHAINABLE_SHARE = 0.8;

export function chainTurnLineages(rows) {
    const all = Array.isArray(rows) ? rows : [];
    if (!all.length) return null;
    const chainable = [];
    const unchained = [];
    for (const r of all) {
        const input = band(r.input);
        const output = band(r.output);
        const cacheRead = band(r.cacheRead);
        const cacheWrite = band(r.cacheWrite);
        if (r.time != null && input != null && output != null && cacheRead != null && cacheWrite != null) {
            chainable.push(r);
        } else {
            unchained.push(r);
        }
    }
    if (chainable.length < all.length * MIN_CHAINABLE_SHARE) return null;
    chainable.sort((a, b) => a.time - b.time);

    const open = []; // { expected, lineage }
    const lineages = [];
    for (const r of chainable) {
        const cr = Number(r.cacheRead);
        const ctx = cr + Number(r.cacheWrite) + Number(r.input);
        let best = null;
        let bestDiff = null;
        for (const o of open) {
            const diff = Math.abs(cr - o.expected);
            if (diff <= TOL_ABS + TOL_REL * o.expected && (bestDiff === null || diff < bestDiff)) {
                best = o;
                bestDiff = diff;
            }
        }
        if (!best) {
            const lineage = {
                kind: lineages.length === 0 ? "root" : cr <= FRESH_START_CEILING ? "fork" : "continuation",
                rows: [],
            };
            lineages.push(lineage);
            best = { expected: 0, lineage };
            open.push(best);
        }
        best.lineage.rows.push(r);
        best.expected = ctx;
    }

    for (const L of lineages) {
        L.start = L.rows[0].time;
        L.end = L.rows[L.rows.length - 1].time;
        L.calls = L.rows.length;
        L.peakCtx = L.rows.reduce((a, r) => Math.max(a, Number(r.cacheRead) + Number(r.cacheWrite) + Number(r.input)), 0);
    }
    lineages.sort((a, b) => (a.kind === "root" ? -1 : b.kind === "root" ? 1 : a.start - b.start));
    return { lineages, unchained };
}

const restatLineage = (L) => ({
    ...L,
    start: L.rows[0].time,
    end: L.rows[L.rows.length - 1].time,
    calls: L.rows.length,
    peakCtx: L.rows.reduce((a, r) => Math.max(a, Number(r.cacheRead) + Number(r.cacheWrite) + Number(r.input)), 0),
});

/**
 * Content-corrects chain membership at compaction seams. The summarizer call
 * is captured token-only, so its bands nearest-join onto the post-compaction
 * window's first call, and cache arithmetic then files that call as the OLD
 * chain's last member - the small cache regression sits inside chaining
 * tolerance. A row that OPENS with a compaction banner cannot continue an
 * older conversation, so a non-head row whose resolved opening is a
 * compaction with a summary DIFFERENT from its own chain head's is re-homed:
 * into the chain whose head opens with the same summary (fragment equality),
 * else - only when its own head is positively another kind of opening - into
 * a chain of its own. An unresolved head is not evidence: without a positive
 * fragment match elsewhere the row stays put, since the head could be this
 * row's own window with a failed opening fetch.
 *
 * Pure. Returns the model object unchanged when nothing moves.
 */
export function evictMisfiledCompactionRows(model, openings) {
    if (!model?.lineages?.length || !openings) return model;
    const openingOf = (row) => openings[String(row?.traceId || "")] || null;
    const fragmentsOf = (row) => {
        const o = openingOf(row);
        return o?.kind === "compaction" && Array.isArray(o.compactionFragments) && o.compactionFragments.length
            ? o.compactionFragments
            : null;
    };
    const sameFragments = (a, b) => !!a && !!b && a.length === b.length && a.every((f, i) => f === b[i]);

    const moves = [];
    for (let li = 0; li < model.lineages.length; li++) {
        const L = model.lineages[li];
        const headFrags = fragmentsOf(L.rows[0]);
        const headOpening = openingOf(L.rows[0]);
        for (let ri = 1; ri < L.rows.length; ri++) {
            const frags = fragmentsOf(L.rows[ri]);
            if (!frags || sameFragments(frags, headFrags)) continue;
            let target = -1;
            for (let ti = 0; ti < model.lineages.length; ti++) {
                if (ti !== li && sameFragments(frags, fragmentsOf(model.lineages[ti].rows[0]))) {
                    target = ti;
                    break;
                }
            }
            if (target < 0 && (!headOpening || headOpening.kind === "compaction")) continue;
            moves.push({ li, ri, target });
        }
    }
    if (!moves.length) return model;

    const rowsByLineage = model.lineages.map((L) => L.rows.slice());
    const spawned = [];
    for (const m of moves.reverse()) {
        const [row] = rowsByLineage[m.li].splice(m.ri, 1);
        if (m.target >= 0) {
            const rows = rowsByLineage[m.target];
            let at = rows.findIndex((r) => r.time > row.time);
            if (at < 0) at = rows.length;
            rows.splice(at, 0, row);
        } else {
            spawned.push({
                kind: Number(row.cacheRead) <= FRESH_START_CEILING ? "fork" : "continuation",
                rows: [row],
            });
        }
    }
    const lineages = [];
    for (let i = 0; i < model.lineages.length; i++) {
        if (rowsByLineage[i].length) lineages.push(restatLineage({ ...model.lineages[i], rows: rowsByLineage[i] }));
    }
    for (const L of spawned) lineages.push(restatLineage(L));
    lineages.sort((a, b) => (a.kind === "root" ? -1 : b.kind === "root" ? 1 : a.start - b.start));
    return { ...model, lineages };
}

// ── Opening verification ─────────────────────────────────────────────────────
// A fresh cache start means "the model saw a near-empty cache", which two very
// different events produce: a genuinely new conversation (a sub-agent's first
// request - it opens with a USER PROMPT), and the same thread re-sent after
// mechanical context editing (the harness dropped earlier turns / tool results
// to save context - the request then BEGINS mid-conversation, with an
// assistant thinking block, a tool_use, or a bare tool_result). The stored
// first request of the chain settles which one it was.

/**
 * Classify a chain's first stored request from its parsed turns.
 *
 * Returns { kind: "prompt", text } when the first non-system turn is a user
 * message carrying real text (a genuine conversation opening - `text` is the
 * opening prompt), { kind: "mid" } when the request starts mid-conversation
 * (context-edited re-send), and { kind: "unknown" } when there is nothing to
 * read (no turns / unparsable body).
 */
// Harness-injected framing inside a first user message (claudeMd context,
// environment notes) - not the opening the person or spawner wrote. Stripped
// before the text is used as a label or matched against spawn prompts.
const SYSTEM_REMINDER_SPAN = /<system-reminder>[\s\S]*?(?:<\/system-reminder>|$)/g;

function withoutReminders(text) {
    return String(text || "").replace(SYSTEM_REMINDER_SPAN, " ").replace(/\s+/g, " ").trim();
}

export function classifyThreadOpening(turns) {
    const list = Array.isArray(turns) ? turns : [];
    const first = list.find((t) => t?.role !== "system");
    if (!first) return { kind: "unknown" };
    if (first.role !== "user") return { kind: "mid" };
    let text = "";
    let sawReminderOnly = false;
    for (const b of first.blocks || []) {
        if (b?.type === "text" && typeof b.text === "string" && b.text.trim()) {
            const cleaned = withoutReminders(b.text);
            if (cleaned) {
                text = cleaned;
                break;
            }
            // Reminder-only block: harness framing ahead of the real prompt.
            // Keep scanning - the authored text is often the next block.
            sawReminderOnly = true;
            continue;
        }
        // A tool_result before any text means the request resumes an existing
        // exchange - the "user" turn is the harness returning results, not a
        // person or spawner opening a conversation.
        if (b?.type === "tool_result") return { kind: "mid" };
    }
    if (text) return { kind: "prompt", text };
    // All-reminder opening: a genuine conversation start with no authored
    // text to show. Still a fresh conversation, so not "mid" - the label
    // falls back to the call summary.
    return sawReminderOnly ? { kind: "prompt", text: "" } : { kind: "mid" };
}

// Prefix length that decides "this opening IS that spawn prompt". Assignments
// run to thousands of chars; two distinct assignments diverge long before
// this, while capture-side trailing truncation diverges after it.
const SPAWN_MATCH_PREFIX = 200;

// How far an opening runs with a spawn prompt, in characters, both compared
// whitespace-collapsed (the opening arrives collapsed by withoutReminders).
// Sibling spawns cut from one template share a preamble that can run past the
// match prefix: a fan-out of reviewers, each told the same rules before its
// own rubric. The prefix says an opening could be the prompt's; when several
// could, the longest run says whose it is.
export function spawnPromptAgreement(openingText, spawnPrompt) {
    const opening = String(openingText || "").trim();
    const spawn = String(spawnPrompt || "").replace(/\s+/g, " ").trim();
    const n = Math.min(opening.length, spawn.length);
    let i = 0;
    while (i < n && opening.charCodeAt(i) === spawn.charCodeAt(i)) i++;
    return i;
}

/**
 * True when a thread's opening text matches one of the spawn prompts the
 * reader's conversation issued - the exact evidence that THIS conversation
 * spawned that thread. Two match modes, both literal string evidence:
 * prefix equality for direct assignments, and containment for fan-out
 * sources (a workflow script carries its child prompts verbatim mid-text,
 * so the opening appears INSIDE the spawn text rather than at its head).
 */
export function matchesSpawnPrompt(openingText, spawnPrompts) {
    const opening = String(openingText || "").trim();
    if (!opening) return false;
    const probe = opening.slice(0, SPAWN_MATCH_PREFIX);
    for (const p of spawnPrompts || []) {
        // The opening text arrives whitespace-COLLAPSED (withoutReminders);
        // a raw multi-line spawn prompt must be collapsed the same way or
        // the prefix comparison dies at its first newline.
        const spawn = String(p || "").replace(/\s+/g, " ").trim();
        if (!spawn) continue;
        const n = Math.min(SPAWN_MATCH_PREFIX, spawn.length, opening.length);
        if (n > 20 && opening.slice(0, n) === spawn.slice(0, n)) return true;
        if (probe.length > 20 && spawn.includes(probe)) return true;
    }
    return false;
}
